/* eslint-disable react-hooks/rules-of-hooks */
import { cookieService } from '../components/cookieService';
import { tagPlausibleCustomerType } from '../hooks/useAnalytics';
import { Size, useWindowSize } from '../hooks/useWindowSize';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { createContainer } from 'unstated-next';
import { CustomerType } from '../types';

interface Ui {
	isCartOpen: boolean;
	isMenuOpen: boolean;
	isCustomerTypeModalOpen: boolean;
	isFavoritesOpen: boolean;
	isTopBannerOpen: boolean;
	isFilterPanelOpen: boolean;
	isSpecialPricesModalOpen: boolean;
	isScrollingPrevented: boolean;
	isLoginModalVisible: boolean;
	isHeaderSticky: boolean;
	isFilterButtonsSticky: boolean;
	categoryHasDescription: boolean;
	categoyHasExpandableDescription: boolean;
	isSearchFormFocused: boolean;
	priceWithVat: boolean;
	windowSize: Size;
	// will only be defined when a new customer selects a customer type from modal. it's needed so
	// we can know when the `customer-type` cookie has been set for new customers
	customerType: CustomerType | undefined;
	toggleCart(): void;
	toggleMenu(): void;
	showCustomerTypeModal(visible: boolean): void;
	toggleFavorites(): void;
	toggleFilterPanel(): void;
	setSpecialPricesModalIsOpen(open: boolean): void;
	showLoginModal(visible: boolean): void;
	setStickyHeader(sticky: boolean): void;
	setStickyFilterButtons(sticky: boolean): void;
	setHasDescription(description: boolean): void;
	setHasExpandableDescription(expandableDescription: boolean): void;
	setIsSearchFormFocused(focus: boolean): void;
	setPriceWithVat(withVat: boolean): void;
	togglePriceWithVat(): void;
	setCustomerType(type: CustomerType): void;
	setTopBannerOpen(open: boolean): void;
}

function buildUi(): Ui {
	let [isCartOpen, setIsCartOpen] = useState(false);
	let [isMenuOpen, setIsMenuOpen] = useState(false);
	let [isCustomerTypeModalOpen, setIsCustomerTypeModalOpen] = useState(false);
	let [isFavoritesOpen, setIsFavoritesOpen] = useState(false);
	let [isTopBannerOpen, setIsTopBannerOpen] = useState(true);
	let [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
	let [isSpecialPricesModalOpen, setIsSpecialPricesModalOpen] = useState(false);
	let [isLoginModalVisible, setIsLoginModalVisiable] = useState(false);
	let [isHeaderSticky, setIsHeaderSticky] = useState(false);
	let [isFilterButtonsSticky, setIsFilterButtonsSticky] = useState(false);
	let [isSearchFormFocused, setIsSearchFormFocused] = useState(false);
	let [categoryHasDescription, setCategoryHasDescription] = useState(false);
	let [categoyHasExpandableDescription, setCategoryHasExpandableDescription] = useState(false);
	let [customerType, setCustomerType] = useState<CustomerType>();

	const { search } = useLocation();

	let [priceWithVat, setPriceWithVat] = useState(search.indexOf('from=gpla') >= 0);

	useEffect(() => {
		setPriceWithVat(
			cookieService.read('customer-type') === 'private' ||
				(search.indexOf('from=gpla') >= 0 && !cookieService.read('customer-type'))
		);

		setIsCustomerTypeModalOpen(cookieService.read('customer-type') ? false : true);
	}, []);

	const windowSize: Size = useWindowSize();

	/*
	 * Memoised for the same reason as the product container: this is read all over the page, the
	 * hook above reads the location, and a fresh object on every route change re-renders every
	 * reader - product boxes included - however little has actually changed.
	 */
	return useMemo(
		() => ({
			isCartOpen,
			isMenuOpen,
			isCustomerTypeModalOpen,
			isFavoritesOpen,
			isTopBannerOpen,
			isFilterPanelOpen,
			isSpecialPricesModalOpen,
			isScrollingPrevented:
				isMenuOpen ||
				(isCartOpen && windowSize.width && windowSize.width < 2670) ||
				isFavoritesOpen ||
				isLoginModalVisible ||
				isFilterPanelOpen ||
				isSpecialPricesModalOpen,
			isLoginModalVisible,
			isHeaderSticky,
			isFilterButtonsSticky,
			categoryHasDescription,
			categoyHasExpandableDescription,
			isSearchFormFocused,
			priceWithVat,
			windowSize,
			setPriceWithVat,
			customerType,
			toggleCart() {
				setIsCartOpen(!isCartOpen);
			},
			toggleMenu() {
				setIsMenuOpen(!isMenuOpen);
			},
			showCustomerTypeModal(visible: boolean) {
				setIsCustomerTypeModalOpen(visible);
			},
			toggleFavorites() {
				setIsFavoritesOpen(!isFavoritesOpen);
			},
			toggleFilterPanel() {
				setIsFilterPanelOpen(!isFilterPanelOpen);
			},
			setSpecialPricesModalIsOpen(open: boolean) {
				setIsSpecialPricesModalOpen(open);
			},
			showLoginModal(visible) {
				setIsLoginModalVisiable(visible);
			},
			setStickyHeader(sticky) {
				setIsHeaderSticky(sticky);
			},
			setStickyFilterButtons(sticky) {
				setIsFilterButtonsSticky(sticky);
			},
			setHasDescription(description) {
				setCategoryHasDescription(description);
			},
			setHasExpandableDescription(description) {
				setCategoryHasExpandableDescription(description);
			},
			togglePriceWithVat() {
				setPriceWithVat(!priceWithVat);
			},
			setIsSearchFormFocused(focused) {
				setIsSearchFormFocused(focused);
			},
			setCustomerType(type) {
				setCustomerType(type);
				tagPlausibleCustomerType(type);
			},
			setTopBannerOpen(open) {
				setIsTopBannerOpen(open);
			},
		}),
		[
			isCartOpen,
			isMenuOpen,
			isCustomerTypeModalOpen,
			isFavoritesOpen,
			isTopBannerOpen,
			isFilterPanelOpen,
			isSpecialPricesModalOpen,
			isLoginModalVisible,
			isHeaderSticky,
			isFilterButtonsSticky,
			categoryHasDescription,
			categoyHasExpandableDescription,
			isSearchFormFocused,
			priceWithVat,
			windowSize,
			customerType,
		]
	);
}

export const UiContainer = createContainer(buildUi);
